import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import PageAlert from "../../components/PageAlert/PageAlert";
import { fetchEnquiry, updateEnquiryStatus } from "../../api/enquiryApi";
import { resolveApiError } from "../../utils/apiError";
import "./enquiries.scss";

const statusLabel = {
  new: "New",
  in_progress: "In progress",
  closed: "Closed",
};

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const parseSpecifications = (value) => {
  if (Array.isArray(value)) return value.filter((item) => item && typeof item === "object");
  if (typeof value !== "string") return [];
  const trimmed = value.trim();
  if (!trimmed) return [];
  try {
    const parsed = JSON.parse(trimmed);
    return Array.isArray(parsed) ? parsed.filter((item) => item && typeof item === "object") : [];
  } catch {
    return [{ label: "Specification", value: trimmed }];
  }
};

const formatSpecPair = (english, chinese) => {
  const en = String(english || "").trim();
  const zh = String(chinese || "").trim();
  if (en && zh) return `${en} · ${zh}`;
  return en || zh;
};

const mapEnquiry = (record = {}) => {
  const items = (record.items || record.product_lines || []).map((item, index) => ({
    id: item.id || `${record.id}-${index}`,
    productId: item.product_id ?? item.productId ?? null,
    productName: item.product_name || item.productName || "",
    specifications: parseSpecifications(item.specifications || item.specification || ""),
    quantity: item.quantity ?? 0,
  }));

  return {
    id: record.id,
    enquiryNumber: record.enquiry_number || record.enquiryNumber || record.id,
    fullName: record.full_name || record.fullName || "",
    company: record.company || "",
    deliveryLocation: record.delivery_location || record.deliveryLocation || "",
    notes: record.notes || record.customer_remarks || "",
    status: record.status || "new",
    submittedAt: record.submitted_at || record.created_at || record.submittedAt,
    items,
  };
};

const EnquiryDetailPage = () => {
  const { enquiryId } = useParams();
  const navigate = useNavigate();
  const [enquiry, setEnquiry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    let ignore = false;
    const loadEnquiry = async () => {
      setLoading(true);
      setError("");
      try {
        const record = await fetchEnquiry(enquiryId);
        if (!ignore) setEnquiry(mapEnquiry(record));
      } catch (requestError) {
        if (!ignore) {
          setEnquiry(null);
          resolveApiError(requestError, setError, navigate);
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    loadEnquiry();
    return () => {
      ignore = true;
    };
  }, [enquiryId, navigate]);

  const totalQty = useMemo(
    () => (enquiry?.items || []).reduce((sum, item) => sum + Number(item.quantity || 0), 0),
    [enquiry]
  );

  const saveStatus = async () => {
    if (!enquiry) return;
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const record = await updateEnquiryStatus(enquiry.id, enquiry.status);
      if (record?.status) {
        setEnquiry((current) => ({ ...current, status: record.status }));
      }
      setNotice(`Status updated to ${statusLabel[enquiry.status] || enquiry.status}.`);
    } catch (requestError) {
      resolveApiError(requestError, setError, navigate);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="enquiry-detail">
        <div className="enquiry-detail__top">
          <div>
            <Link to="/admin/enquiries"><Icon icon="ph:arrow-left" height={14} /> Back to enquiries</Link>
            <h2>Loading enquiry…</h2>
          </div>
        </div>
      </div>
    );
  }

  if (!enquiry) {
    return (
      <div className="enquiries-page">
        {error && <PageAlert variant="error" onDismiss={() => setError("")}>{error}</PageAlert>}
        <div className="enquiries-page__toolbar">
          <div>
            <Link to="/admin/enquiries"><Icon icon="ph:arrow-left" height={14} /> Back to enquiries</Link>
            <h2>Enquiry not found</h2>
            <p>This enquiry could not be loaded.</p>
          </div>
          <button className="ui-button" type="button" onClick={() => navigate("/admin/enquiries")}>
            Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="enquiry-detail">
      {notice && (
        <PageAlert variant="success" onDismiss={() => setNotice("")}>{notice}</PageAlert>
      )}
      {error && (
        <PageAlert variant="error" onDismiss={() => setError("")}>{error}</PageAlert>
      )}

      <div className="enquiry-detail__top">
        <div>
          <Link to="/admin/enquiries"><Icon icon="ph:arrow-left" height={14} /> Back to enquiries</Link>
          <h2>Enquiry #{enquiry.enquiryNumber}</h2>
          <p>Submitted {formatDate(enquiry.submittedAt)} · {enquiry.items.length} product line{enquiry.items.length === 1 ? "" : "s"} · {totalQty} total qty</p>
        </div>
        <div className="enquiry-detail__top-actions">
          <select
            value={enquiry.status}
            disabled={saving}
            onChange={(event) => {
              setEnquiry((current) => ({ ...current, status: event.target.value }));
              setNotice("");
              setError("");
            }}
          >
            <option value="new">New</option>
            <option value="in_progress">In progress</option>
            <option value="closed">Closed</option>
          </select>
          <button
            className="ui-button ui-button--primary"
            type="button"
            disabled={saving}
            onClick={saveStatus}
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      <div className="enquiry-detail__layout">
        <main>
          <section className="enquiry-detail__card">
            <header>
              <p>REQUESTED PRODUCTS</p>
              <h3>Product lines</h3>
            </header>

            <div className="enquiry-detail__items">
              {enquiry.items.map((item, index) => (
                  <article key={item.id} className="enquiry-detail__item">
                    <div className="enquiry-detail__item-head">
                      <strong>Line {index + 1}</strong>
                      <span className={item.productId ? "is-catalog" : "is-custom"}>
                        {item.productId ? "Catalogue" : "Custom"}
                      </span>
                    </div>

                    <div className="enquiry-detail__fields">
                      <label>
                        Product name
                        <input value={item.productName} readOnly tabIndex={-1} />
                      </label>
                      <label>
                        Quantity
                        <input value={item.quantity} readOnly tabIndex={-1} />
                      </label>
                      <div className="enquiry-detail__specs">
                        <span>Specifications chosen</span>
                        {item.specifications.length ? (
                          <ul>
                            {item.specifications.map((spec, specIndex) => {
                              const label = formatSpecPair(spec.label, spec.label_zh);
                              const value = formatSpecPair(spec.value, spec.value_zh);
                              if (!label && !value) return null;
                              return (
                                <li key={`${item.id}-spec-${specIndex}`}>
                                  {label ? <strong>{label}</strong> : null}
                                  {value ? <span>{value}</span> : null}
                                </li>
                              );
                            })}
                          </ul>
                        ) : (
                          <p>—</p>
                        )}
                      </div>
                    </div>
                  </article>
              ))}
            </div>
          </section>

          <section className="enquiry-detail__card">
            <header>
              <p>NOTES</p>
              <h3>Customer remarks</h3>
            </header>
            <p className="enquiry-detail__notes">
              {enquiry.notes?.trim() || "No additional notes provided."}
            </p>
          </section>
        </main>

        <aside>
          <section className="enquiry-detail__card">
            <header>
              <p>CUSTOMER</p>
              <h3>Contact details</h3>
            </header>
            <dl className="enquiry-detail__meta">
              <div>
                <dt>Full name</dt>
                <dd>{enquiry.fullName}</dd>
              </div>
              <div>
                <dt>Company</dt>
                <dd>{enquiry.company || "—"}</dd>
              </div>
              <div>
                <dt>Delivery location</dt>
                <dd>{enquiry.deliveryLocation || "—"}</dd>
              </div>
              <div>
                <dt>Status</dt>
                <dd>
                  <span className={`enquiries-page__status enquiries-page__status--${enquiry.status}`}>
                    {statusLabel[enquiry.status] || enquiry.status}
                  </span>
                </dd>
              </div>
            </dl>
          </section>
        </aside>
      </div>
    </div>
  );
};

export default EnquiryDetailPage;
